import { useState, useEffect } from 'react'
import {
  Box,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material'
import {
  Add,
  Send,
  RateReview,
  Check,
  Close,
  Inventory,
} from '@mui/icons-material'
import { PageHeader, Button, DataTable, Card, PermissionGate, usePermissions } from '@components/common'
import api from '@services/api'
import { toast } from 'react-toastify'

const STATUS_COLORS = {
  draft: 'default',
  submitted: 'warning',
  under_review: 'info',
  approved: 'success',
  rejected: 'error',
  ordered: 'primary',
  received: 'success',
  cancelled: 'default',
}

const PRIORITY_COLORS = {
  low: 'default',
  medium: 'info',
  high: 'warning',
  urgent: 'error',
}

const REQUISITION_TYPES = [
  { value: 'new_equipment', label: 'New Equipment Request' },
  { value: 'swap', label: 'Equipment Swap' },
  { value: 'replacement', label: 'Replacement' },
  { value: 'additional', label: 'Additional Equipment' },
]

const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'urgent', label: 'Urgent' },
]

const WORKFLOW_STEPS = ['Draft', 'Submitted', 'Under Review', 'Approved', 'Ordered', 'Received']

const columns = [
  { field: 'requisition_number', headerName: 'Req #', sortable: true, width: 130 },
  { field: 'equipment_name', headerName: 'Equipment', sortable: true },
  {
    field: 'requisition_type',
    headerName: 'Type',
    sortable: true,
    renderCell: (value) => value?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()),
  },
  { field: 'department_name', headerName: 'Department', sortable: true },
  {
    field: 'priority',
    headerName: 'Priority',
    sortable: true,
    renderCell: (value) => (
      <Chip
        label={value?.toUpperCase()}
        color={PRIORITY_COLORS[value] || 'default'}
        size="small"
      />
    ),
  },
  {
    field: 'status',
    headerName: 'Status',
    sortable: true,
    renderCell: (value) => (
      <Chip
        label={value?.replace('_', ' ').toUpperCase()}
        color={STATUS_COLORS[value] || 'default'}
        size="small"
      />
    ),
  },
  { field: 'requested_by_name', headerName: 'Requested By', sortable: true },
  {
    field: 'created_at',
    headerName: 'Date',
    sortable: true,
    renderCell: (value) => value ? new Date(value).toLocaleDateString() : '-',
  },
]

const initialFormData = {
  requisition_type: 'new_equipment',
  equipment_name: '',
  equipment_category: '',
  quantity: 1,
  specifications: '',
  swap_asset: '',
  department: '',
  location: '',
  priority: 'medium',
  justification: '',
  current_situation: '',
  estimated_cost: '',
  notes: '',
}

const RequisitionsList = () => {
  const { hasPermission } = usePermissions()
  const [requisitions, setRequisitions] = useState([])
  const [loading, setLoading] = useState(true)
  const [formOpen, setFormOpen] = useState(false)
  const [detailOpen, setDetailOpen] = useState(false)
  const [selectedReq, setSelectedReq] = useState(null)
  const [formData, setFormData] = useState(initialFormData)
  const [formLoading, setFormLoading] = useState(false)
  const [errors, setErrors] = useState({})

  // Options for dropdowns
  const [categories, setCategories] = useState([])
  const [departments, setDepartments] = useState([])
  const [locations, setLocations] = useState([])
  const [assets, setAssets] = useState([])

  const fetchRequisitions = async () => {
    try {
      setLoading(true)
      const response = await api.get('/assets/requisitions/')
      setRequisitions(response.data.results || response.data)
    } catch (error) {
      console.error('Error fetching requisitions:', error)
      toast.error('Failed to load requisitions')
    } finally {
      setLoading(false)
    }
  }

  const fetchOptions = async () => {
    try {
      const [catsRes, deptsRes, locsRes, assetsRes] = await Promise.all([
        api.get('/assets/categories/'),
        api.get('/common/departments/'),
        api.get('/assets/locations/'),
        api.get('/assets/assets/'),
      ])
      setCategories(catsRes.data.results || catsRes.data)
      setDepartments(deptsRes.data.results || deptsRes.data)
      setLocations(locsRes.data.results || locsRes.data)
      setAssets(assetsRes.data.results || assetsRes.data)
    } catch (error) {
      console.error('Error fetching options:', error)
    }
  }

  useEffect(() => {
    fetchRequisitions()
    fetchOptions()
  }, [])

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }))
    }
  }

  const handleSubmit = async () => {
    if (!formData.equipment_name || !formData.department) {
      setErrors({
        equipment_name: !formData.equipment_name ? 'Equipment name is required' : '',
        department: !formData.department ? 'Department is required' : '',
      })
      return
    }

    setFormLoading(true)
    try {
      await api.post('/assets/requisitions/', {
        ...formData,
        equipment_category: formData.equipment_category || null,
        swap_asset: formData.swap_asset || null,
        location: formData.location || null,
        estimated_cost: formData.estimated_cost || null,
      })
      fetchRequisitions()
      setFormOpen(false)
      setFormData(initialFormData)
      toast.success('Requisition created successfully')
    } catch (error) {
      console.error('Error creating requisition:', error)
      if (error.response?.data) {
        const backendErrors = {}
        Object.entries(error.response.data).forEach(([key, value]) => {
          backendErrors[key] = Array.isArray(value) ? value[0] : value
        })
        setErrors(backendErrors)
        toast.error('Please fix the errors in the form')
      } else {
        toast.error('Failed to create requisition')
      }
    } finally {
      setFormLoading(false)
    }
  }

  const handleAction = async (action, reqId) => {
    try {
      await api.post(`/assets/requisitions/${reqId}/${action}/`)
      fetchRequisitions()
      setDetailOpen(false)
      toast.success(`Requisition ${action}ed successfully`)
    } catch (error) {
      console.error(`Error performing ${action}:`, error)
      toast.error(`Failed to ${action} requisition`)
    }
  }

  const handleRowClick = (row) => {
    setSelectedReq(row)
    setDetailOpen(true)
  }

  const getStepIndex = (status) => {
    const statusMap = {
      draft: 0,
      submitted: 1,
      under_review: 2,
      approved: 3,
      ordered: 4,
      received: 5,
    }
    return statusMap[status] ?? 0
  }

  const getActionButtons = (req) => {
    if (!req) return null

    switch (req.status) {
      case 'draft':
        return hasPermission('requisitions.submit') ? (
          <Button
            startIcon={<Send />}
            onClick={() => handleAction('submit', req.id)}
          >
            Submit
          </Button>
        ) : null
      case 'submitted':
        return hasPermission('requisitions.review') ? (
          <Button
            startIcon={<RateReview />}
            onClick={() => handleAction('review', req.id)}
          >
            Start Review
          </Button>
        ) : null
      case 'under_review':
        return hasPermission('requisitions.approve') ? (
          <>
            <Button
              startIcon={<Check />}
              color="success"
              onClick={() => handleAction('approve', req.id)}
            >
              Approve
            </Button>
            <Button
              startIcon={<Close />}
              color="error"
              variant="outlined"
              onClick={() => handleAction('reject', req.id)}
            >
              Reject
            </Button>
          </>
        ) : null
      case 'approved':
        return hasPermission('requisitions.fulfill') ? (
          <Button
            startIcon={<Inventory />}
            color="success"
            onClick={() => handleAction('fulfill', req.id)}
          >
            Mark Fulfilled
          </Button>
        ) : null
      default:
        return null
    }
  }

  return (
    <Box>
      <PageHeader
        title="Equipment Requisitions"
        subtitle="Request and track new equipment"
        breadcrumbs={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Assets', path: '/assets' },
          { label: 'Requisitions' },
        ]}
        actions={
          <PermissionGate permission="requisitions.create">
            <Button startIcon={<Add />} onClick={() => setFormOpen(true)}>
              New Requisition
            </Button>
          </PermissionGate>
        }
      />

      <Card>
        <DataTable
          columns={columns}
          data={requisitions}
          loading={loading}
          onRowClick={handleRowClick}
          searchPlaceholder="Search requisitions..."
          emptyMessage="No requisitions found"
        />
      </Card>

      {/* Create Requisition Dialog */}
      <Dialog open={formOpen} onClose={() => setFormOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>New Equipment Requisition</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Requisition Type</InputLabel>
                <Select
                  name="requisition_type"
                  value={formData.requisition_type}
                  onChange={handleChange}
                  label="Requisition Type"
                >
                  {REQUISITION_TYPES.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Priority</InputLabel>
                <Select
                  name="priority"
                  value={formData.priority}
                  onChange={handleChange}
                  label="Priority"
                >
                  {PRIORITY_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                label="Equipment Name"
                name="equipment_name"
                value={formData.equipment_name}
                onChange={handleChange}
                error={Boolean(errors.equipment_name)}
                helperText={errors.equipment_name}
                required
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Quantity"
                name="quantity"
                type="number"
                value={formData.quantity}
                onChange={handleChange}
                inputProps={{ min: 1 }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Equipment Category</InputLabel>
                <Select
                  name="equipment_category"
                  value={formData.equipment_category}
                  onChange={handleChange}
                  label="Equipment Category"
                >
                  <MenuItem value="">None</MenuItem>
                  {categories.map((cat) => (
                    <MenuItem key={cat.id} value={cat.id}>
                      {cat.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Estimated Cost"
                name="estimated_cost"
                type="number"
                value={formData.estimated_cost}
                onChange={handleChange}
              />
            </Grid>

            {formData.requisition_type === 'swap' && (
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Asset to Swap</InputLabel>
                  <Select
                    name="swap_asset"
                    value={formData.swap_asset}
                    onChange={handleChange}
                    label="Asset to Swap"
                  >
                    <MenuItem value="">None</MenuItem>
                    {assets.map((asset) => (
                      <MenuItem key={asset.id} value={asset.id}>
                        {asset.asset_tag} - {asset.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

            <Grid item xs={12} md={6}>
              <FormControl fullWidth error={Boolean(errors.department)} required>
                <InputLabel>Department</InputLabel>
                <Select
                  name="department"
                  value={formData.department}
                  onChange={handleChange}
                  label="Department"
                >
                  {departments.map((dept) => (
                    <MenuItem key={dept.id} value={dept.id}>
                      {dept.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Location</InputLabel>
                <Select
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                  label="Location"
                >
                  <MenuItem value="">None</MenuItem>
                  {locations.map((loc) => (
                    <MenuItem key={loc.id} value={loc.id}>
                      {loc.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Specifications"
                name="specifications"
                value={formData.specifications}
                onChange={handleChange}
                multiline
                rows={2}
                placeholder="Technical specifications or requirements..."
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Justification"
                name="justification"
                value={formData.justification}
                onChange={handleChange}
                multiline
                rows={2}
                placeholder="Why is this equipment needed?"
                required
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Current Situation"
                name="current_situation"
                value={formData.current_situation}
                onChange={handleChange}
                multiline
                rows={2}
                placeholder="Describe current equipment state or lack thereof..."
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Additional Notes"
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                multiline
                rows={2}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setFormOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} loading={formLoading}>
            Create Requisition
          </Button>
        </DialogActions>
      </Dialog>

      {/* Requisition Detail Dialog */}
      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Requisition {selectedReq?.requisition_number}
        </DialogTitle>
        <DialogContent>
          {selectedReq && (
            <>
              {/* Workflow Stepper */}
              {selectedReq.status !== 'rejected' && selectedReq.status !== 'cancelled' && (
                <Box sx={{ mb: 3, mt: 2 }}>
                  <Stepper activeStep={getStepIndex(selectedReq.status)} alternativeLabel>
                    {WORKFLOW_STEPS.map((label) => (
                      <Step key={label}>
                        <StepLabel>{label}</StepLabel>
                      </Step>
                    ))}
                  </Stepper>
                </Box>
              )}

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="h6">{selectedReq.equipment_name}</Typography>
                  <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                    <Chip
                      label={selectedReq.status?.replace('_', ' ').toUpperCase()}
                      color={STATUS_COLORS[selectedReq.status] || 'default'}
                      size="small"
                    />
                    <Chip
                      label={selectedReq.priority?.toUpperCase()}
                      color={PRIORITY_COLORS[selectedReq.priority] || 'default'}
                      size="small"
                    />
                    <Chip
                      label={selectedReq.requisition_type?.replace('_', ' ')}
                      variant="outlined"
                      size="small"
                    />
                  </Box>
                </Grid>

                <Grid item xs={6}>
                  <strong>Quantity:</strong> {selectedReq.quantity}
                </Grid>
                <Grid item xs={6}>
                  <strong>Department:</strong> {selectedReq.department_name}
                </Grid>
                <Grid item xs={6}>
                  <strong>Category:</strong> {selectedReq.equipment_category_name || 'N/A'}
                </Grid>
                <Grid item xs={6}>
                  <strong>Estimated Cost:</strong> {selectedReq.estimated_cost ? `UGX ${Number(selectedReq.estimated_cost).toLocaleString()}` : 'N/A'}
                </Grid>

                {selectedReq.specifications && (
                  <Grid item xs={12}>
                    <strong>Specifications:</strong>
                    <Typography variant="body2" color="text.secondary">
                      {selectedReq.specifications}
                    </Typography>
                  </Grid>
                )}

                {selectedReq.justification && (
                  <Grid item xs={12}>
                    <strong>Justification:</strong>
                    <Typography variant="body2" color="text.secondary">
                      {selectedReq.justification}
                    </Typography>
                  </Grid>
                )}

                {selectedReq.current_situation && (
                  <Grid item xs={12}>
                    <strong>Current Situation:</strong>
                    <Typography variant="body2" color="text.secondary">
                      {selectedReq.current_situation}
                    </Typography>
                  </Grid>
                )}

                <Grid item xs={6}>
                  <strong>Requested By:</strong> {selectedReq.requested_by_name}
                </Grid>
                <Grid item xs={6}>
                  <strong>Date:</strong> {selectedReq.created_at ? new Date(selectedReq.created_at).toLocaleDateString() : 'N/A'}
                </Grid>

                {selectedReq.reviewed_by_name && (
                  <Grid item xs={6}>
                    <strong>Reviewed By:</strong> {selectedReq.reviewed_by_name}
                  </Grid>
                )}

                {selectedReq.approved_by_name && (
                  <Grid item xs={6}>
                    <strong>Approved By:</strong> {selectedReq.approved_by_name}
                  </Grid>
                )}

                {selectedReq.rejection_reason && (
                  <Grid item xs={12}>
                    <strong>Rejection Reason:</strong>
                    <Typography variant="body2" color="error">
                      {selectedReq.rejection_reason}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setDetailOpen(false)}>
            Close
          </Button>
          {getActionButtons(selectedReq)}
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default RequisitionsList
